import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { Users, CreditCard, Activity, Shield, LogOut, CheckCircle, XCircle, Trash2, UserPlus, FileText, Calendar, Eye, EyeOff, UserCircle, Sun, Moon, AlertCircle, Search, DollarSign, TrendingUp, PieChart as PieChartIcon, MessageCircle, RefreshCw, Clock, Settings } from 'lucide-react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import PaymentRequestsManagement from './PaymentRequestsManagement';
import UserSearchModal from '../components/UserSearchModal';

const SuperAdminDashboard = () => {
  const [showUserSearchModal, setShowUserSearchModal] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const [stats, setStats] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Estado para crear Super Admin
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', full_name: '', phone: '' });
  const [createLoading, setCreateLoading] = useState(false);
  
  // Estado para reportes
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [monthlyComparison, setMonthlyComparison] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  
  // Estado para métricas seleccionadas
  const [selectedMetrics, setSelectedMetrics] = useState({
    newRegistrations: true,
    licensesActivated: true,
    licensesDeleted: true,
    paymentsApproved: true,
    totalRevenue: true
  });
  
  // Estado para mostrar/ocultar contraseñas
  const [visiblePasswords, setVisiblePasswords] = useState({});
  
  // Estado para búsqueda de clientes
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para IPs y Puertos
  const [vpnResources, setVpnResources] = useState(null);
  
  // Estado para Server VPN
  const [vpnCredentials, setVpnCredentials] = useState(null);
  const [ipFilter, setIpFilter] = useState('all'); // all, used, available
  const [portFilter, setPortFilter] = useState('all'); // all, used, available
  
  // Estado para Configuración del Sistema
  const [systemConfig, setSystemConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  
  // Estado para WhatsApp Widget
  const [whatsappConfig, setWhatsappConfig] = useState({
    enabled: false,
    phone: '',
    message: 'Hola, necesito ayuda con EasyTickets'
  });
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  
  // Estado para Anuncios
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 1,
    icon: 'bell',
    is_active: true,
    show_once: false,
    action_text: '',
    target_audience: 'todos',
    action_link: ''
  });
  
  // Estado para Analytics de Licencias
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Estado para fecha/hora del servidor
  const [serverDateTime, setServerDateTime] = useState(null);
  
  // Estado para estadísticas de versiones de routers
  const [versionStats, setVersionStats] = useState(null);
  
  // Estado para Modo Mantenimiento
  const [maintenanceMode, setMaintenanceMode] = useState({
    login_disabled: false,
    registration_disabled: false,
    message: '🔧 Sistema en Mantenimiento\n\nEstamos realizando mejoras en nuestro sistema.\nPor favor, intenta más tarde.\n\nDisculpa las molestias.'
  });
  const [maintenanceLoading, setMaintenanceLoading] = useState(false);
  
  // Estado para Config de Cron
  const [cronConfig, setCronConfig] = useState(null);
  
  // Estado para Reportes SuperAdmin
  const [reportsData, setReportsData] = useState({
    activatedLicenses: null,
    mikrotikVersions: null,
    licensesRouters: null
  });
  const [reportsLoading, setReportsLoading] = useState(false);

  useEffect(() => {
    loadData();
    loadVpnResources();
    loadMaintenanceConfig();
    loadWhatsAppConfig();
    loadAnalytics(); // Cargar analytics al inicio para mostrar MRR en la tarjeta
    loadServerDateTime(); // Cargar fecha/hora del servidor
    loadVersionStats(); // Cargar estadísticas de versiones RouterOS
    loadCronConfig(); // Cargar config del cron
    loadAnnouncements(); // Cargar anuncios al inicio
  }, []);
  
  const loadVersionStats = async () => {
    try {
      const response = await api.get('/api/admin/routers/version-stats');
      setVersionStats(response.data);
    } catch (error) {
      console.error('Error cargando estadísticas de versiones:', error);
    }
  };
  
  const loadServerDateTime = async () => {
    try {
      const response = await api.get('/api/server/datetime');
      setServerDateTime(response.data);
    } catch (error) {
      console.error('Error cargando fecha/hora del servidor:', error);
    }
  };
  
  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await api.get('/api/superadmin/license-analytics');
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Error cargando analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab]);

  const loadVpnResources = async () => {
    try {
      const response = await api.get('/api/admin/vpn-resources');
      setVpnResources(response.data);
    } catch (error) {
      console.error('Error cargando recursos VPN:', error);
    }
  };
  
  const loadMaintenanceConfig = async () => {
    try {
      const response = await api.get('/api/superadmin/maintenance-config');
      if (response.data) {
        setMaintenanceMode(response.data);
      }
    } catch (error) {
      console.error('Error cargando configuración de mantenimiento:', error);
    }
  };
  
  const saveMaintenanceConfig = async () => {
    setMaintenanceLoading(true);
    try {
      await api.put('/api/superadmin/maintenance-config', maintenanceMode);
      alert('✅ Configuración de mantenimiento guardada exitosamente');
    } catch (error) {
      console.error('Error guardando configuración:', error);
      alert('❌ Error al guardar la configuración');
    } finally {
      setMaintenanceLoading(false);
    }
  };

  const loadWhatsAppConfig = async () => {
    try {
      const response = await api.get('/api/whatsapp/config');
      if (response.data) {
        setWhatsappConfig(response.data);
      }
    } catch (error) {
      console.error('Error cargando configuración de WhatsApp:', error);
    }
  };

  const loadCronConfig = async () => {
    try {
      const response = await api.get('/api/admin/cron-config');
      if (response.data) {
        setCronConfig(response.data);
      }
    } catch (error) {
      console.error('Error cargando configuración de cron:', error);
    }
  };
  
  const loadAnnouncements = async () => {
    try {
      const response = await api.get('/api/superadmin/announcements');
      setAnnouncements(response.data || []);
    } catch (error) {
      console.error('Error cargando anuncios:', error);
    }
  };
  
  const loadSuperAdminReports = async () => {
    setReportsLoading(true);
    try {
      const [activatedRes, versionsRes, licensesRoutersRes] = await Promise.all([
        api.get('/api/superadmin/reports/activated-licenses'),
        api.get('/api/superadmin/reports/mikrotik-versions'),
        api.get('/api/superadmin/reports/licenses-routers')
      ]);
      
      setReportsData({
        activatedLicenses: activatedRes.data,
        mikrotikVersions: versionsRes.data,
        licensesRouters: licensesRoutersRes.data
      });
    } catch (error) {
      console.error('Error cargando reportes:', error);
      alert('❌ Error al cargar los reportes');
    } finally {
      setReportsLoading(false);
    }
  };
  
  
  const saveWhatsAppConfig = async () => {
    setWhatsappLoading(true);
    try {
      await api.post('/api/whatsapp/config', null, {
        params: {
          enabled: whatsappConfig.enabled,
          phone: whatsappConfig.phone,
          message: whatsappConfig.message
        }
      });
      alert('✅ Configuración de WhatsApp guardada exitosamente');
      
      // Recargar la página para que el botón se actualice
      window.location.reload();
    } catch (error) {
      console.error('Error guardando configuración WhatsApp:', error);
      alert('❌ Error al guardar la configuración');
    } finally {
      setWhatsappLoading(false);
    }
  };


  const loadData = async () => {
    try {
      console.log('🔍 Cargando datos del Super Admin...');
      const [statsRes, licensesRes, clientsRes] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/admin/licenses'),
        api.get('/api/admin/clients-summary')
      ]);
      console.log('✅ Stats recibidas:', statsRes.data);
      console.log('✅ Licencias recibidas:', licensesRes.data.length);
      console.log('✅ Clientes recibidos:', clientsRes.data.length);
      setStats(statsRes.data);
      setLicenses(licensesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('❌ Error loading data:', error);
      console.error('❌ Error response:', error.response);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (licenseId) => {
    if (!window.confirm('¿Activar esta licencia?')) return;
    try {
      await api.post(`/api/admin/licenses/${licenseId}/activate`);
      loadData();
    } catch (error) {
      alert('Error al activar licencia');
    }
  };

  const handleSuspend = async (licenseId) => {
    if (!window.confirm('¿Suspender esta licencia?')) return;
    try {
      await api.post(`/api/admin/licenses/${licenseId}/suspend`);
      loadData();
    } catch (error) {
      alert('Error al suspender licencia');
    }
  };

  const handleDelete = async (licenseId) => {
    if (!window.confirm('¿Eliminar esta licencia y limpiar VPN? Esta acción no se puede deshacer.')) return;
    try {
      await api.delete(`/api/admin/licenses/${licenseId}`);
      loadData();
    } catch (error) {
      alert('Error al eliminar licencia');
    }
  };

  const handleDeleteClient = async (userId, clientName) => {
    const confirmed = window.confirm(
      `⚠️ ELIMINACIÓN COMPLETA DE CLIENTE\n\n` +
      `Cliente: ${clientName}\n\n` +
      `Esto eliminará:\n` +
      `• Todas las licencias\n` +
      `• Todos los routers\n` +
      `• Todos los planes\n` +
      `• Todas las fichas/vouchers\n` +
      `• Todos los vendedores\n` +
      `• Todos los puntos de venta\n` +
      `• Todos los secrets VPN\n` +
      `• Todos los puertos NAT\n\n` +
      `Esta acción NO se puede deshacer.\n\n` +
      `¿Estás seguro de continuar?`
    );
    
    if (!confirmed) return;
    
    // Segunda confirmación
    const doubleConfirm = window.confirm(
      `ÚLTIMA CONFIRMACIÓN\n\n` +
      `¿Eliminar definitivamente a ${clientName}?`
    );
    
    if (!doubleConfirm) return;
    
    try {
      setLoading(true);
      const response = await api.delete(`/api/admin/clients/${userId}`);
      
      if (response.data.success) {
        alert(
          `✅ Cliente eliminado exitosamente\n\n` +
          `Recursos liberados:\n` +
          `• ${response.data.secrets_deleted} secrets VPN\n` +
          `• ${response.data.nat_rules_deleted} reglas NAT\n` +
          `• ${response.data.ips_freed} IPs disponibles\n` +
          `• ${response.data.ports_freed} puertos disponibles`
        );
        loadData();
      }
    } catch (error) {
      console.error('Error eliminando cliente:', error);
      alert('❌ Error eliminando cliente: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const badges = {
      trial: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
      deleted: 'bg-black text-white'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      trial: 'Prueba',
      active: 'Activa',
      suspended: 'Suspendida',
      expired: 'Expirada',
      deleted: 'Eliminada'
    };
    return texts[status] || status;
  };

  const togglePasswordVisibility = (clientId, field) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [`${clientId}-${field}`]: !prev[`${clientId}-${field}`]
    }));
  };

  const handleCreateSuperAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.email || !newAdmin.password || !newAdmin.full_name) {
      alert('Por favor completa todos los campos obligatorios');
      return;
    }
    
    setCreateLoading(true);
    try {
      await api.post('/api/admin/create-super-admin', newAdmin);
      alert('Super Administrador creado exitosamente');
      setNewAdmin({ email: '', password: '', full_name: '', phone: '' });
      setShowCreateForm(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al crear Super Admin');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      alert('Por favor selecciona ambas fechas');
      return;
    }
    
    setReportLoading(true);
    try {
      const response = await api.get('/api/admin/reports', {
        params: { start_date: startDate, end_date: endDate }
      });
      setReportData(response.data);
    } catch (error) {
      alert('Error al generar reporte');
    } finally {
      setReportLoading(false);
    }
  };

  const handleMonthlyComparison = async () => {
    setComparisonLoading(true);
    try {
      const response = await api.get('/api/admin/reports/monthly-comparison');
      setMonthlyComparison(response.data);
      setReportData(null); // Limpiar reporte por rango de fechas
    } catch (error) {
      alert('Error al generar comparación mensual');
    } finally {
      setComparisonLoading(false);
    }
  };

  const toggleMetric = (metric) => {
    setSelectedMetrics(prev => ({
      ...prev,
      [metric]: !prev[metric]
    }));
  };

  // Filtrar clientes por búsqueda
  const filteredClients = clients.filter(client => {
    const searchLower = searchTerm.toLowerCase();
    return (
      client.full_name?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.phone?.toLowerCase().includes(searchLower) ||
      client.vpn_username?.toLowerCase().includes(searchLower) ||
      client.vpn_internal_ip?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
    }`}>
      {/* Header */}
      <header className={`shadow-lg transition-colors duration-300 ${
        isDark 
          ? 'bg-gradient-to-r from-purple-900 to-pink-900 border-b border-purple-800' 
          : 'bg-gradient-to-r from-purple-600 to-pink-600'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Shield className="w-10 h-10 text-white" />
              <div>
                <h1 className="text-3xl font-bold text-white">Panel de Super Administrador</h1>
                <p className={isDark ? 'text-purple-200' : 'text-purple-100'}>{user?.full_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                title={isDark ? 'Modo Claro' : 'Modo Oscuro'}
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                <UserCircle className="w-4 h-4" />
                Mi Perfil
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {!loading && stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* 1. Total Clientes - Clickeable */}
            <button
              onClick={() => setActiveTab('clients')}
              className="bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Total Clientes</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold">{clients.length}</p>
              <p className="text-sm text-white/80 mt-2">Usuarios registrados</p>
              {stats?.users_today !== undefined && (
                <div className="mt-3 pt-3 border-t border-white/20">
                  <p className="text-sm text-white/90 font-semibold">
                    Hoy: <span className="text-lg">{stats.users_today}</span> {stats.users_today === 1 ? 'nuevo' : 'nuevos'}
                  </p>
                </div>
              )}
            </button>
            
            {/* 2. Pagos Pendientes - Clickeable */}
            <button
              onClick={() => navigate('/super-admin/payment-reports')}
              className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left relative"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Pagos Pendientes</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold">{stats.pending_payments || 0}</p>
              <p className="text-sm text-white/80 mt-2">Por aprobar</p>
              {stats?.pending_payments > 0 && (
                <div className="absolute top-2 right-2 bg-white text-red-600 text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                  {stats.pending_payments}
                </div>
              )}
            </button>
            
            {/* 3. Recaudación del Mes - Clickeable */}
            <button
              onClick={() => setActiveTab('reports')}
              className="bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Recaudación del Mes</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold">${(stats.monthly_revenue || 0).toFixed(2)}</p>
              <p className="text-sm text-white/80 mt-2">Ingresos mensuales</p>
            </button>

            {/* 4. Recursos VPN - Clickeable */}
            <button
              onClick={() => setActiveTab('vpn-server')}
              className="bg-gradient-to-br from-purple-500 to-violet-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Recursos VPN</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
              </div>
              {vpnResources ? (
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-white/80">IPs Usadas:</span>
                    <span className="text-lg font-bold">{vpnResources.ips_used}/{vpnResources.total_ips}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-white/80">Puertos Usados:</span>
                    <span className="text-lg font-bold">{vpnResources.ports_used}/{vpnResources.total_ports}</span>
                  </div>
                  <div className="mt-2 text-xs text-white/70">
                    Disponibles: {vpnResources.ips_available} IPs, {vpnResources.ports_available} puertos
                  </div>
                </div>
              ) : (
                <p className="text-2xl font-bold">Cargando...</p>
              )}
            </button>

            {/* 5. Anuncios - Clickeable */}
            <button
              onClick={() => setActiveTab('announcements')}
              className="bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Anuncios</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold">{announcements.length}</p>
              <p className="text-sm text-white/80 mt-2">Gestionar anuncios</p>
            </button>

            {/* Videotutoriales - Clickeable */}
            <button
              onClick={() => navigate('/super-admin/video-tutorials')}
              className="bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Videotutoriales</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                  </svg>
                </div>
              </div>
              <p className="text-2xl font-bold">Gestionar</p>
              <p className="text-sm text-white/80 mt-2">Videos de ayuda</p>
            </button>

            {/* WhatsApp Notifications - Clickeable */}
            <button
              onClick={() => navigate('/whatsapp-notifications')}
              className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">WhatsApp</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <MessageCircle className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold">Notificaciones</p>
              <p className="text-sm text-white/80 mt-2">Gestionar mensajes</p>
            </button>

            {/* Gastos - Clickeable */}
            <button
              onClick={() => navigate('/expenses')}
              className="bg-gradient-to-br from-amber-500 to-yellow-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Gastos</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-2xl font-bold">Recordatorios</p>
              <p className="text-sm text-white/80 mt-2">Pagos administrativos</p>
            </button>

            {/* 6. Licenciamiento - Clickeable */}
            <button
              onClick={() => navigate('/license-pricing')}
              className="bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Licenciamiento</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold">{Object.keys(stats).length > 0 ? '✓' : '-'}</p>
              <p className="text-sm text-white/80 mt-2">Configurar precios</p>
            </button>

            {/* 7. Reportes SuperAdmin - Clickeable */}
            <button
              onClick={() => {
                setActiveTab('superadmin-reports');
                loadSuperAdminReports();
              }}
              className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Reportes</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold">📊</p>
              <p className="text-sm text-white/80 mt-2">Análisis y métricas</p>
            </button>

            {/* 8. Configuración - Clickeable */}
            <button
              onClick={() => setActiveTab('config')}
              className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-105 text-left"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white/90">Configuración</h3>
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-4xl font-bold">⚙️</p>
              <p className="text-sm text-white/80 mt-2">Ajustes del sistema</p>
            </button>
          </div>
        )}

        {/* Analytics Card - Quick Access */}
        {!loading && stats && (
          <div className="mb-8">
            <button
              onClick={() => setActiveTab('analytics')}
              className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 rounded-xl shadow-lg p-6 text-white hover:shadow-2xl transition-all transform hover:scale-[1.02]"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-2xl font-bold mb-1">📊 Analytics de Licencias</h3>
                    <p className="text-sm text-white/90">Ver análisis completo de suscripciones e ingresos</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/80 mb-1">MRR Mensual</p>
                  <p className="text-3xl font-bold">
                    ${analyticsData ? analyticsData.summary.total_monthly_revenue.toFixed(2) : (stats.monthly_revenue || 0).toFixed(2)}
                  </p>
                  {analyticsData && (
                    <p className="text-xs text-white/70 mt-1">
                      {analyticsData.summary.paying_users} usuarios de pago
                    </p>
                  )}
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Estadísticas de Versiones RouterOS */}
        {!loading && versionStats && (
          <div className="mb-8">
            <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">📡 Distribución de RouterOS</h3>
                <span className="text-sm text-gray-500">{versionStats.total_routers} routers activos</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className={`${isDark ? 'bg-blue-900/30' : 'bg-blue-50'} rounded-lg p-4`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-blue-600">RouterOS v6</span>
                    <span className="text-2xl font-bold text-blue-600">{versionStats.v6_count}</span>
                  </div>
                  <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${versionStats.v6_percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{versionStats.v6_percentage}% del total</p>
                </div>
                
                <div className={`${isDark ? 'bg-green-900/30' : 'bg-green-50'} rounded-lg p-4`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-600">RouterOS v7</span>
                    <span className="text-2xl font-bold text-green-600">{versionStats.v7_count}</span>
                  </div>
                  <div className="mt-2 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full transition-all"
                      style={{ width: `${versionStats.v7_percentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{versionStats.v7_percentage}% del total</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mb-8">
          <button
            onClick={() => setShowUserSearchModal(true)}
            className="w-full md:w-auto px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 font-semibold"
          >
            <Search className="w-5 h-5" />
            🔍 Buscar y Gestionar Usuario
          </button>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-t-xl shadow-sm">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex space-x-8 px-6 min-w-max" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Resumen
              </button>
              <button
                onClick={() => setActiveTab('licenses')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'licenses'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Licencias ({licenses.length})
              </button>
              <button
                onClick={() => setActiveTab('clients')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'clients'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Clientes ({clients.length})
              </button>
              <button
                onClick={() => setActiveTab('vpn-server')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'vpn-server'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Server VPN
              </button>
              <button
                onClick={() => setActiveTab('superadmins')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'superadmins'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Super Admins
              </button>
              <button
                onClick={() => setActiveTab('config')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'config'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Configuración
              </button>
              <button
                onClick={() => setActiveTab('announcements')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'announcements'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Anuncios
              </button>
              <button
                onClick={() => setActiveTab('pricing')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pricing'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Licencias
              </button>
              <button
                onClick={() => navigate('/super-admin/payment-reports')}
                className={`py-4 px-1 border-b-2 font-medium text-sm relative border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300`}
              >
                Reportes de Pago
                {stats?.pending_payments > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {stats.pending_payments}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'analytics'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📊 Analytics
              </button>
              <button
                onClick={() => {
                  setActiveTab('superadmin-reports');
                  loadSuperAdminReports();
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'superadmin-reports'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                📈 Reportes Sistema
              </button>
              <button
                onClick={() => setActiveTab('reports')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'reports'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Reportes
              </button>
            </nav>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-b-xl shadow-sm p-6">
          {activeTab === 'overview' && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Bienvenido al Panel de Control</h2>
              
              {/* Alerta de Capacidad */}
              {vpnResources && vpnResources.used_ips >= (vpnResources.total_ips * 0.9) && (
                <div className={`mb-6 p-4 rounded-lg border-l-4 ${
                  vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                    ? 'bg-red-50 border-red-500'
                    : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                    ? 'bg-orange-50 border-orange-500'
                    : 'bg-yellow-50 border-yellow-500'
                }`}>
                  <div className="flex items-start">
                    <AlertCircle className={`w-6 h-6 mr-3 ${
                      vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                        ? 'text-red-600'
                        : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                        ? 'text-orange-600'
                        : 'text-yellow-600'
                    }`} />
                    <div>
                      <h3 className={`font-semibold ${
                        vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                          ? 'text-red-800'
                          : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                          ? 'text-orange-800'
                          : 'text-yellow-800'
                      }`}>
                        {vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                          ? '🚨 Capacidad Crítica'
                          : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                          ? '⚠️ Capacidad Casi Completa'
                          : '⚡ Capacidad Alta'}
                      </h3>
                      <p className={`text-sm mt-1 ${
                        vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                          ? 'text-red-700'
                          : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                          ? 'text-orange-700'
                          : 'text-yellow-700'
                      }`}>
                        {vpnResources.used_ips} de {vpnResources.total_ips} IPs en uso 
                        ({Math.round((vpnResources.used_ips / vpnResources.total_ips) * 100)}% de capacidad)
                      </p>
                      <p className={`text-sm mt-2 ${
                        vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                          ? 'text-red-700'
                          : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                          ? 'text-orange-700'
                          : 'text-yellow-700'
                      }`}>
                        {vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                          ? '⚠️ Se recomienda pausar registros y expandir capacidad del servidor VPN.'
                          : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                          ? '⚠️ Planifica la expansión del servidor VPN pronto.'
                          : 'Monitorea la capacidad regularmente.'}
                      </p>
                      <button
                        onClick={() => setActiveTab('config')}
                        className={`mt-3 px-4 py-2 rounded text-sm font-medium ${
                          vpnResources.used_ips >= (vpnResources.total_ips * 0.98)
                            ? 'bg-red-600 hover:bg-red-700 text-white'
                            : vpnResources.used_ips >= (vpnResources.total_ips * 0.95)
                            ? 'bg-orange-600 hover:bg-orange-700 text-white'
                            : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                        }`}
                      >
                        Ir a Configuración
                      </button>
                    </div>
                  </div>
                </div>
              )}
              <p className="text-gray-600 mb-6">
                Desde aquí puedes gestionar todas las licencias, usuarios y pagos del sistema EasyTickets.
              </p>
              
              {/* Widget de Fecha/Hora del Servidor */}
              {serverDateTime && (
                <div className="mb-6 border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center">
                    <Calendar className="w-5 h-5 text-blue-600 mr-3" />
                    <div>
                      <h3 className="font-semibold text-blue-900 text-sm">Fecha y Hora del Servidor VPS</h3>
                      <p className="text-blue-700 text-lg font-medium mt-1">
                        {serverDateTime.formatted}
                      </p>
                      <p className="text-xs text-blue-600 mt-1">
                        Zona horaria: {serverDateTime.timezone}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Gestionar Licencias</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Ve a la pestaña de Licencias para activar, suspender o eliminar licencias de clientes.
                  </p>
                  <button
                    onClick={() => setActiveTab('licenses')}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                  >
                    Ver Licencias
                  </button>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Estadísticas</h3>
                  <p className="text-sm text-gray-600">
                    Monitorea el crecimiento del sistema y el estado de todas las licencias en tiempo real.
                  </p>
                </div>
                <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                  <h3 className="font-semibold text-red-900 mb-2">🗑️ Gestión de Base de Datos</h3>
                  <p className="text-sm text-red-700 mb-3">
                    Eliminar colecciones de datos (⚠️ Zona peligrosa)
                  </p>
                  <button
                    onClick={() => window.location.href = '/database-management'}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm w-full"
                  >
                    Acceder a Gestión de BD
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'clients' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : clients.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay clientes</h3>
                  <p className="text-gray-600">Aún no hay usuarios registrados en el sistema.</p>
                </div>
              ) : (
                <div>
                  {/* Buscador */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email, teléfono, usuario VPN o IP..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                    {searchTerm && (
                      <p className="text-sm text-gray-600 mt-2">
                        Mostrando {filteredClients.length} de {clients.length} clientes
                      </p>
                    )}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre Cliente</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contraseña</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Registro</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha Activación</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Teléfono</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario VPN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contraseña VPN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP VPN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Puerto</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredClients.map((client) => (
                          <tr key={client.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-medium text-gray-900">{client.full_name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{client.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm text-gray-900">
                                  {visiblePasswords[`${client.id}-reg`] ? client.password_plain : '••••••••'}
                                </span>
                                <button
                                  onClick={() => togglePasswordVisibility(client.id, 'reg')}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  {visiblePasswords[`${client.id}-reg`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{formatDate(client.created_at)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{formatDate(client.license_activation_date)}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-600">{client.phone || 'N/A'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-mono text-sm text-gray-900">{client.vpn_username}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm text-gray-900">
                                  {visiblePasswords[`${client.id}-vpn`] ? client.vpn_password : '••••••••'}
                                </span>
                                <button
                                  onClick={() => togglePasswordVisibility(client.id, 'vpn')}
                                  className="text-gray-400 hover:text-gray-600"
                                >
                                  {visiblePasswords[`${client.id}-vpn`] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-mono text-sm text-gray-900">{client.vpn_internal_ip}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-mono text-sm text-gray-900">{client.vpn_external_port}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                client.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}>
                                {client.is_active ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={() => handleDeleteClient(client.id, client.full_name)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Eliminar cliente y todos sus datos"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'licenses' && (
            <div>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              ) : licenses.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay licencias</h3>
                  <p className="text-gray-600">Aún no hay usuarios registrados en el sistema.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vencimiento</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VPN</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {licenses.map((license) => (
                        <tr key={license.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <div className="font-medium text-gray-900">{license.user?.full_name}</div>
                              <div className="text-sm text-gray-500">{license.user?.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="font-semibold text-gray-900 uppercase">{license.plan_type || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(license.status)}`}>
                              {getStatusText(license.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(license.expiry_date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-xs">
                              <div className="font-mono text-gray-900">{license.vpn_internal_ip}</div>
                              <div className="text-gray-500">Puerto: {license.vpn_external_port}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {license.status !== 'active' && license.status !== 'deleted' && (
                                <button
                                  onClick={() => handleActivate(license.id)}
                                  className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Activar"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                              )}
                              {license.status === 'active' && (
                                <button
                                  onClick={() => handleSuspend(license.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Suspender"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              )}
                              {license.status !== 'deleted' && (
                                <button
                                  onClick={() => handleDelete(license.id)}
                                  className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                  title="Eliminar"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'superadmins' && (
            <div>
              <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Gestión de Super Administradores</h2>
                  <button
                    onClick={() => setShowCreateForm(!showCreateForm)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    {showCreateForm ? 'Cancelar' : 'Crear Super Admin'}
                  </button>
                </div>

                {showCreateForm && (
                  <div className="bg-gray-50 rounded-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Nuevo Super Administrador</h3>
                    <form onSubmit={handleCreateSuperAdmin} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre Completo *
                        </label>
                        <input
                          type="text"
                          value={newAdmin.full_name}
                          onChange={(e) => setNewAdmin({ ...newAdmin, full_name: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={newAdmin.email}
                          onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Contraseña *
                        </label>
                        <input
                          type="password"
                          value={newAdmin.password}
                          onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          required
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          value={newAdmin.phone}
                          onChange={(e) => setNewAdmin({ ...newAdmin, phone: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                      
                      <div className="md:col-span-2 flex gap-3">
                        <button
                          type="submit"
                          disabled={createLoading}
                          className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                        >
                          {createLoading ? 'Creando...' : 'Crear Super Admin'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateForm(false);
                            setNewAdmin({ email: '', password: '', full_name: '', phone: '' });
                          }}
                          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Nota:</strong> Los Super Administradores tienen acceso completo al sistema y pueden gestionar licencias, ver reportes y crear otros Super Admins.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'vpn-server' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Servidor VPN</h2>
                
                {/* Recursos Reservados CRÍTICOS */}
                <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 mb-6 border-2 border-red-300">
                  <h3 className="text-lg font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    ⚠️ RECURSOS RESERVADOS - NO USAR
                  </h3>
                  <p className="text-sm text-red-700 mb-4">Estos puertos e IPs están reservados para servicios críticos del sistema</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 border border-red-200">
                      <h4 className="font-semibold text-gray-900 mb-2">🔒 Puertos Reservados</h4>
                      <div className="space-y-1 text-sm">
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">22</span> - SSH</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">80</span> - HTTP</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">443</span> - HTTPS</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">21</span> - FTP</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">23</span> - Telnet</p>
                        <p className="text-red-700 font-semibold"><span className="font-mono bg-red-100 px-2 py-1 rounded">1709</span> - Mikrotik Reservado (CRÍTICO)</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">8728</span> - API Mikrotik</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">8729</span> - API Mikrotik SSL</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">8291</span> - WinBox</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">9291</span> - API Custom (Servidor VPN)</p>
                        <p className="text-gray-700"><span className="font-mono bg-gray-100 px-2 py-1 rounded">8080</span> - Proxy Web</p>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-green-200">
                      <h4 className="font-semibold text-gray-900 mb-2">✅ Rangos Seguros</h4>
                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="font-semibold text-green-700 mb-1">Puertos para Clientes:</p>
                          <p className="text-gray-700"><span className="font-mono bg-green-100 px-2 py-1 rounded">20000 - 21022</span></p>
                          <p className="text-xs text-gray-500 mt-1">1,023 puertos disponibles (Capacidad: ~1,000 routers)</p>
                        </div>
                        <div>
                          <p className="font-semibold text-green-700 mb-1">IPs Internas VPN:</p>
                          <p className="text-gray-700"><span className="font-mono bg-green-100 px-2 py-1 rounded">10.10.0.2 - 10.10.3.254</span></p>
                          <p className="text-xs text-gray-500 mt-1">~1,021 IPs disponibles</p>
                        </div>
                        <div>
                          <p className="font-semibold text-blue-700 mb-1">IP Servidor VPN:</p>
                          <p className="text-gray-700"><span className="font-mono bg-blue-100 px-2 py-1 rounded">10.3.96.7</span></p>
                          <p className="text-xs text-gray-500 mt-1">IP fija del servidor (no asignar)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Credenciales del Servidor */}
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-6 mb-6 border border-blue-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Credenciales de Acceso</h3>
                  {vpnCredentials ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
                        <div className="bg-white px-4 py-2 rounded border border-gray-300 font-mono text-sm">
                          {vpnCredentials.host}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Puerto</label>
                        <div className="bg-white px-4 py-2 rounded border border-gray-300 font-mono text-sm">
                          {vpnCredentials.port}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Usuario</label>
                        <div className="bg-white px-4 py-2 rounded border border-gray-300 font-mono text-sm">
                          {vpnCredentials.user}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
                        <div className="bg-white px-4 py-2 rounded border border-gray-300 font-mono text-sm">
                          {vpnCredentials.password}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={async () => {
                        try {
                          const response = await api.get('/api/superadmin/vpn-server-info');
                          setVpnCredentials({
                            ...response.data.credentials,
                            ips: response.data.ips,
                            ports: response.data.ports
                          });
                          setVpnResources(response.data.summary);
                        } catch (error) {
                          alert('Error al cargar credenciales del VPN');
                        }
                      }}
                      className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Cargar Información del Servidor
                    </button>
                  )}
                </div>

                {/* Resumen de Recursos */}
                {vpnResources && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">IPs</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-semibold">{vpnResources.total_ips}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Usadas:</span>
                          <span className="font-semibold text-red-600">{vpnResources.ips_used}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Disponibles:</span>
                          <span className="font-semibold text-green-600">{vpnResources.ips_available}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Puertos</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Total:</span>
                          <span className="font-semibold">{vpnResources.total_ports}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Usados:</span>
                          <span className="font-semibold text-red-600">{vpnResources.ports_used}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Disponibles:</span>
                          <span className="font-semibold text-green-600">{vpnResources.ports_available}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Lista de IPs y Puertos */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* IPs */}
                  <div className="bg-white rounded-lg shadow border border-gray-200">
                    <div className="p-4 border-b border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900">Direcciones IP</h4>
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => setIpFilter('all')}
                          className={`px-3 py-1 rounded text-sm ${ipFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Todas
                        </button>
                        <button
                          onClick={() => setIpFilter('used')}
                          className={`px-3 py-1 rounded text-sm ${ipFilter === 'used' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Usadas
                        </button>
                        <button
                          onClick={() => setIpFilter('available')}
                          className={`px-3 py-1 rounded text-sm ${ipFilter === 'available' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Disponibles
                        </button>
                      </div>
                    </div>
                    <div className="p-4 max-h-96 overflow-y-auto">
                      {vpnCredentials && vpnCredentials.ips ? (
                        <div className="space-y-1 font-mono text-sm">
                          {vpnCredentials.ips
                            .filter(ip => ipFilter === 'all' || ip.status === ipFilter)
                            .map((ip, index) => (
                              <div
                                key={index}
                                className={`px-3 py-2 rounded ${ip.status === 'used' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
                              >
                                {ip.ip} - {ip.status === 'used' ? 'Usada' : 'Disponible'}
                              </div>
                            ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Carga las credenciales para ver la lista</p>
                      )}
                    </div>
                  </div>

                  {/* Puertos */}
                  <div className="bg-white rounded-lg shadow border border-gray-200">
                    <div className="p-4 border-b border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-900">Puertos</h4>
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={() => setPortFilter('all')}
                          className={`px-3 py-1 rounded text-sm ${portFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Todos
                        </button>
                        <button
                          onClick={() => setPortFilter('used')}
                          className={`px-3 py-1 rounded text-sm ${portFilter === 'used' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Usados
                        </button>
                        <button
                          onClick={() => setPortFilter('available')}
                          className={`px-3 py-1 rounded text-sm ${portFilter === 'available' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                        >
                          Disponibles
                        </button>
                      </div>
                    </div>
                    <div className="p-4 max-h-96 overflow-y-auto">
                      {vpnCredentials && vpnCredentials.ports ? (
                        <div className="space-y-1 font-mono text-sm">
                          {vpnCredentials.ports
                            .filter(port => portFilter === 'all' || port.status === portFilter)
                            .map((port, index) => (
                              <div
                                key={index}
                                className={`px-3 py-2 rounded ${port.status === 'used' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
                              >
                                {port.port} - {port.status === 'used' ? 'Usado' : 'Disponible'}
                              </div>
                            ))}
                        </div>
                      ) : (
                        <p className="text-gray-500 text-center py-4">Carga las credenciales para ver la lista</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Configuración del Sistema</h2>
                <p className="text-gray-600">Ajusta parámetros globales del sistema</p>
              </div>

              {/* Botón de Acceso Rápido a Configuración de Cron */}
              <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-600" />
                      Suspensión Automática PPPoE
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      Configura cada cuánto tiempo el sistema revisa y suspende clientes vencidos
                    </p>
                    <p className="text-xs text-gray-500">
                      Configuración actual: cada {cronConfig?.interval_hours || 2} horas
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/cron-config')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold flex items-center gap-2"
                  >
                    <Settings className="h-5 w-5" />
                    Configurar Cron
                  </button>
                </div>
              </div>

              {/* Widget de WhatsApp - Siempre visible */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg shadow-lg p-6 mb-6 border-2 border-green-200">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Widget de WhatsApp</h3>
                    <p className="text-sm text-gray-600">Configura el botón flotante de contacto</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Toggle Activar/Desactivar */}
                  <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
                    <div>
                      <h4 className="font-semibold text-gray-900">Activar Widget</h4>
                      <p className="text-sm text-gray-600">Muestra el botón flotante en todas las páginas</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={whatsappConfig.enabled}
                        onChange={(e) => setWhatsappConfig({...whatsappConfig, enabled: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                    </label>
                  </div>

                  {/* Número de WhatsApp */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Número de WhatsApp (con código de país)
                    </label>
                    <input
                      type="text"
                      value={whatsappConfig.phone}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, phone: e.target.value})}
                      placeholder="521234567890"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ejemplo: 521234567890 (52 para México, sin espacios ni guiones)
                    </p>
                  </div>

                  {/* Mensaje predeterminado */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">
                      Mensaje Predeterminado
                    </label>
                    <textarea
                      value={whatsappConfig.message}
                      onChange={(e) => setWhatsappConfig({...whatsappConfig, message: e.target.value})}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Hola, necesito ayuda con EasyTickets"
                    />
                  </div>

                  {/* Vista Previa */}
                  {whatsappConfig.enabled && whatsappConfig.phone && (
                    <div className="bg-green-100 border-2 border-green-300 rounded-lg p-4">
                      <p className="text-sm font-semibold text-green-800 mb-2">Vista Previa:</p>
                      <p className="text-sm text-green-700">
                        📱 Número: {whatsappConfig.phone}
                      </p>
                      <p className="text-sm text-green-700">
                        💬 Mensaje: &ldquo;{whatsappConfig.message}&rdquo;
                      </p>
                    </div>
                  )}

                  {/* Botón Guardar */}
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={saveWhatsAppConfig}
                      disabled={whatsappLoading || !whatsappConfig.phone}
                      className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                    >
                      {whatsappLoading ? 'Guardando...' : 'Guardar Configuración de WhatsApp'}
                    </button>
                  </div>
                </div>
              </div>

              {!systemConfig ? (
                <button
                  onClick={async () => {
                    setConfigLoading(true);
                    try {
                      const response = await api.get('/api/superadmin/system-config');
                      setSystemConfig(response.data);
                    } catch (error) {
                      alert('Error al cargar configuración');
                    } finally {
                      setConfigLoading(false);
                    }
                  }}
                  disabled={configLoading}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {configLoading ? 'Cargando...' : 'Cargar Configuración'}
                </button>
              ) : (
                <div className="space-y-6">
                  {/* Licencias y Trial */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Licencias y Período de Prueba</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración del Trial (días)
                        </label>
                        <input
                          type="number"
                          value={systemConfig.trial_duration_days}
                          onChange={(e) => setSystemConfig({...systemConfig, trial_duration_days: parseInt(e.target.value)})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Días para eliminar cuenta sin pago (post-trial)
                        </label>
                        <input
                          type="number"
                          value={systemConfig.trial_auto_delete_days}
                          onChange={(e) => setSystemConfig({...systemConfig, trial_auto_delete_days: parseInt(e.target.value)})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Eliminar cuenta inactiva después de (días)
                        </label>
                        <input
                          type="number"
                          value={systemConfig.inactive_account_delete_days}
                          onChange={(e) => setSystemConfig({...systemConfig, inactive_account_delete_days: parseInt(e.target.value)})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Pagos y Suspensión */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Pagos y Suspensión</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Período de gracia después de vencimiento (días)
                        </label>
                        <input
                          type="number"
                          value={systemConfig.payment_grace_period_days}
                          onChange={(e) => setSystemConfig({...systemConfig, payment_grace_period_days: parseInt(e.target.value)})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">Días antes de suspender cuenta por falta de pago</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Recordatorios de pago (días antes)
                        </label>
                        <input
                          type="text"
                          value={systemConfig.payment_reminder_days.join(', ')}
                          onChange={(e) => setSystemConfig({
                            ...systemConfig, 
                            payment_reminder_days: e.target.value.split(',').map(d => parseInt(d.trim()))
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                          placeholder="7, 3, 1"
                        />
                        <p className="text-xs text-gray-500 mt-1">Ejemplo: 7, 3, 1 (separado por comas)</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Control de Capacidad del Sistema</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Máximo de clientes totales
                        </label>
                        <input
                          type="number"
                          value={systemConfig.capacity_limits?.max_total_clients || 500}
                          onChange={(e) => setSystemConfig({
                            ...systemConfig, 
                            capacity_limits: {
                              ...systemConfig.capacity_limits,
                              max_total_clients: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Pausar registros al (%)
                        </label>
                        <input
                          type="number"
                          min="80"
                          max="100"
                          value={systemConfig.capacity_limits?.pause_registrations_at_percent || 98}
                          onChange={(e) => setSystemConfig({
                            ...systemConfig, 
                            capacity_limits: {
                              ...systemConfig.capacity_limits,
                              pause_registrations_at_percent: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Alerta de advertencia (%)
                        </label>
                        <input
                          type="number"
                          min="70"
                          max="95"
                          value={systemConfig.capacity_limits?.warning_threshold_percent || 90}
                          onChange={(e) => setSystemConfig({
                            ...systemConfig, 
                            capacity_limits: {
                              ...systemConfig.capacity_limits,
                              warning_threshold_percent: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Alerta crítica (%)
                        </label>
                        <input
                          type="number"
                          min="85"
                          max="98"
                          value={systemConfig.capacity_limits?.critical_threshold_percent || 95}
                          onChange={(e) => setSystemConfig({
                            ...systemConfig, 
                            capacity_limits: {
                              ...systemConfig.capacity_limits,
                              critical_threshold_percent: parseInt(e.target.value)
                            }
                          })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mensaje de pausa personalizado
                      </label>
                      <textarea
                        value={systemConfig.capacity_limits?.custom_pause_message || ''}
                        onChange={(e) => setSystemConfig({
                          ...systemConfig, 
                          capacity_limits: {
                            ...systemConfig.capacity_limits,
                            custom_pause_message: e.target.value
                          }
                        })}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Mensaje que verán los usuarios al intentar registrarse..."
                      />
                    </div>
                    
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={systemConfig.capacity_limits?.auto_pause_enabled || false}
                        onChange={(e) => setSystemConfig({
                          ...systemConfig, 
                          capacity_limits: {
                            ...systemConfig.capacity_limits,
                            auto_pause_enabled: e.target.checked
                          }
                        })}
                        className="w-5 h-5 text-purple-600"
                      />
                      <span className="text-sm text-gray-700">Activar pausa automática al alcanzar límite</span>
                    </label>
                  </div>

                  {/* Notificaciones */}
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Notificaciones</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(systemConfig.notifications).map(([key, value]) => (
                        <label key={key} className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => {
                              const newNotifications = {...systemConfig.notifications};
                              newNotifications[key] = e.target.checked;
                              setSystemConfig({...systemConfig, notifications: newNotifications});
                            }}
                            className="w-5 h-5 text-purple-600"
                          />
                          <span className="text-sm text-gray-700 capitalize">{key.replace(/_/g, ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Botones de acción */}
                  <div className="flex justify-end gap-4">
                    <button
                      onClick={() => {
                        if (confirm('¿Descartar cambios?')) {
                          setSystemConfig(null);
                        }
                      }}
                      className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={async () => {
                        setConfigLoading(true);
                        try {
                          await api.put('/api/superadmin/system-config', systemConfig);
                          alert('Configuración actualizada exitosamente');
                        } catch (error) {
                          alert('Error al actualizar configuración');
                        } finally {
                          setConfigLoading(false);
                        }
                      }}
                      disabled={configLoading}
                      className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                    >
                      {configLoading ? 'Guardando...' : 'Guardar Cambios'}
                    </button>
                  </div>

                  {systemConfig.updated_at && (
                    <p className="text-sm text-gray-500 text-right">
                      Última actualización: {new Date(systemConfig.updated_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === 'announcements' && (
            <div>
              <div className="mb-6 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Gestión de Anuncios</h2>
                  <p className="text-gray-600">Crea y gestiona anuncios para los administradores</p>
                </div>
                <button
                  onClick={() => {
                    setShowAnnouncementForm(true);
                    setEditingAnnouncement(null);
                    setAnnouncementForm({
                      title: '',
                      message: '',
                      type: 'info',
                      priority: 1,
                      icon: 'bell',
                      is_active: true,
                      show_once: false,
                      action_text: '',
                      action_link: ''
                    });
                  }}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <FileText className="w-5 h-5" />
                  Nuevo Anuncio
                </button>
              </div>

              {/* Formulario de Anuncio */}
              {showAnnouncementForm && (
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    {editingAnnouncement ? 'Editar Anuncio' : 'Crear Nuevo Anuncio'}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Título *</label>
                      <input
                        type="text"
                        value={announcementForm.title}
                        onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Título del anuncio..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Mensaje *</label>
                      <textarea
                        value={announcementForm.message}
                        onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                        rows="4"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        placeholder="Mensaje del anuncio..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                        <select
                          value={announcementForm.type}
                          onChange={(e) => setAnnouncementForm({...announcementForm, type: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="info">Información</option>
                          <option value="warning">Advertencia</option>
                          <option value="critical">Crítico</option>
                          <option value="success">Éxito</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Prioridad</label>
                        <select
                          value={announcementForm.priority}
                          onChange={(e) => setAnnouncementForm({...announcementForm, priority: parseInt(e.target.value)})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="1">Normal</option>
                          <option value="2">Importante</option>
                          <option value="3">Urgente</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Icono</label>
                        <select
                          value={announcementForm.icon}
                          onChange={(e) => setAnnouncementForm({...announcementForm, icon: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="bell">Campana</option>
                          <option value="alert-triangle">Triángulo de Alerta</option>
                          <option value="info">Información</option>
                          <option value="check-circle">Check Circle</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Campos nuevos para acción con link */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">🔗 Botón de Acción (Opcional)</h4>
                      <p className="text-xs text-gray-500 mb-3">Agrega un botón que lleve a los usuarios a una página específica (interna o externa)</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Texto del botón</label>
                          <input
                            type="text"
                            value={announcementForm.action_text || ''}
                            onChange={(e) => setAnnouncementForm({...announcementForm, action_text: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            placeholder="Ej: Ver más información"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Link del botón</label>
                          <input
                            type="text"
                            value={announcementForm.action_link || ''}
                            onChange={(e) => setAnnouncementForm({...announcementForm, action_link: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                            placeholder="Ej: /routers o https://ejemplo.com"
                          />
                          <div className="flex items-start gap-2 mt-2">
                            <div className="flex-1">
                              <p className="text-xs text-blue-600 font-medium">📱 Páginas internas:</p>
                              <p className="text-xs text-gray-500">Usa /routers, /plans, /pppoe, etc.</p>
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-green-600 font-medium">🌐 Enlaces externos:</p>
                              <p className="text-xs text-gray-500">Usa https://ejemplo.com</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Selector de Audiencia */}
                    <div className="border-t pt-4 mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">👥 ¿Quién verá este anuncio?</label>
                      <select
                        value={announcementForm.target_audience || 'todos'}
                        onChange={(e) => setAnnouncementForm({...announcementForm, target_audience: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="todos">📢 Todos los usuarios</option>
                        <option value="tiene_v7">🔷 Usuarios con AL MENOS 1 router v7</option>
                        <option value="tiene_v6">🔶 Usuarios con AL MENOS 1 router v6</option>
                        <option value="solo_v7">🔷 Usuarios con TODOS sus routers v7</option>
                        <option value="solo_v6">🔶 Usuarios con TODOS sus routers v6</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Filtra quién verá el anuncio según las versiones de sus routers MikroTik
                      </p>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={announcementForm.is_active}
                          onChange={(e) => setAnnouncementForm({...announcementForm, is_active: e.target.checked})}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm text-gray-700">Activo</span>
                      </label>
                      
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={announcementForm.show_once}
                          onChange={(e) => setAnnouncementForm({...announcementForm, show_once: e.target.checked})}
                          className="w-4 h-4 text-purple-600"
                        />
                        <span className="text-sm text-gray-700">Mostrar solo una vez</span>
                      </label>
                    </div>
                    
                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <button
                        onClick={() => {
                          setShowAnnouncementForm(false);
                          setEditingAnnouncement(null);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={async () => {
                          setAnnouncementsLoading(true);
                          try {
                            if (editingAnnouncement) {
                              await api.put(`/api/superadmin/announcements/${editingAnnouncement.id}`, announcementForm);
                              alert('Anuncio actualizado');
                            } else {
                              await api.post('/api/superadmin/announcements', announcementForm);
                              alert('Anuncio creado');
                            }
                            setShowAnnouncementForm(false);
                            setEditingAnnouncement(null);
                            // Recargar anuncios
                            const response = await api.get('/api/superadmin/announcements');
                            setAnnouncements(response.data);
                          } catch (error) {
                            alert('Error al guardar anuncio');
                          } finally {
                            setAnnouncementsLoading(false);
                          }
                        }}
                        disabled={!announcementForm.title || !announcementForm.message || announcementsLoading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                      >
                        {announcementsLoading ? 'Guardando...' : (editingAnnouncement ? 'Actualizar' : 'Crear')}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Lista de Anuncios */}
              <div className="bg-white rounded-lg shadow">
                {!announcements.length && !announcementsLoading ? (
                  <button
                    onClick={async () => {
                      setAnnouncementsLoading(true);
                      try {
                        const response = await api.get('/api/superadmin/announcements');
                        setAnnouncements(response.data);
                      } catch (error) {
                        alert('Error al cargar anuncios');
                      } finally {
                        setAnnouncementsLoading(false);
                      }
                    }}
                    disabled={announcementsLoading}
                    className="w-full p-8 text-center text-gray-600 hover:bg-gray-50"
                  >
                    {announcementsLoading ? 'Cargando...' : 'Haz clic para cargar anuncios'}
                  </button>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {announcements.map((announcement) => (
                      <div key={announcement.id} className="p-6 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">{announcement.title}</h3>
                              {announcement.priority >= 2 && (
                                <span className="px-2 py-1 text-xs font-bold bg-red-100 text-red-800 rounded">
                                  {announcement.priority === 3 ? 'URGENTE' : 'IMPORTANTE'}
                                </span>
                              )}
                              <span className={`px-2 py-1 text-xs rounded ${
                                announcement.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {announcement.is_active ? 'Activo' : 'Inactivo'}
                              </span>
                              <span className={`px-2 py-1 text-xs rounded ${
                                announcement.type === 'critical' ? 'bg-red-100 text-red-800' :
                                announcement.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                                announcement.type === 'success' ? 'bg-green-100 text-green-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                                {announcement.type}
                              </span>
                            </div>
                            <p className="text-gray-600 mb-3">{announcement.message}</p>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>👁️ {announcement.views_count} vistas</span>
                              <span>✅ {announcement.dismissed_count} cerrados</span>
                              <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => {
                                setEditingAnnouncement(announcement);
                                setAnnouncementForm({
                                  title: announcement.title,
                                  message: announcement.message,
                                  type: announcement.type,
                                  priority: announcement.priority,
                                  icon: announcement.icon,
                                  is_active: announcement.is_active,
                                  show_once: announcement.show_once,
                                  action_text: announcement.action_text || '',
                                  action_link: announcement.action_link || ''
                                });
                                setShowAnnouncementForm(true);
                              }}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <FileText className="w-5 h-5" />
                            </button>
                            <button
                              onClick={async () => {
                                if (confirm('¿Eliminar este anuncio?')) {
                                  try {
                                    await api.delete(`/api/superadmin/announcements/${announcement.id}`);
                                    setAnnouncements(announcements.filter(a => a.id !== announcement.id));
                                    alert('Anuncio eliminado');
                                  } catch (error) {
                                    alert('Error al eliminar');
                                  }
                                }
                              }}
                              className="p-2 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Modo Mantenimiento / Pánico */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg shadow-lg p-6 mb-6 border-2 border-red-200">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Modo Mantenimiento / Pánico</h3>
                    <p className="text-sm text-gray-600">Controla el acceso al sistema y muestra mensajes personalizados</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Toggle para Login */}
                  <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Bloquear Login</h4>
                        <p className="text-sm text-gray-600">Desactiva el inicio de sesión</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={maintenanceMode.login_disabled}
                        onChange={(e) => setMaintenanceMode({...maintenanceMode, login_disabled: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                    </label>
                  </div>

                  {/* Toggle para Registro */}
                  <div className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <UserPlus className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Bloquear Registro</h4>
                        <p className="text-sm text-gray-600">Desactiva nuevos registros</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={maintenanceMode.registration_disabled}
                        onChange={(e) => setMaintenanceMode({...maintenanceMode, registration_disabled: e.target.checked})}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                    </label>
                  </div>

                  {/* Mensaje Personalizado */}
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Mensaje Personalizado</label>
                    <textarea
                      value={maintenanceMode.message}
                      onChange={(e) => setMaintenanceMode({...maintenanceMode, message: e.target.value})}
                      rows="4"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Escribe el mensaje que verán los usuarios..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Este mensaje se mostrará cuando login o registro estén bloqueados</p>
                  </div>

                  {/* Vista Previa */}
                  {(maintenanceMode.login_disabled || maintenanceMode.registration_disabled) && (
                    <div className="bg-gray-900/90 p-6 rounded-lg">
                      <p className="text-xs text-gray-400 mb-2">Vista Previa del Popup:</p>
                      <div className="bg-white rounded-lg p-6 text-center">
                        <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-3" />
                        <div className="whitespace-pre-line text-gray-700">{maintenanceMode.message}</div>
                      </div>
                    </div>
                  )}

                  {/* Botones de Acción */}
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      onClick={loadMaintenanceConfig}
                      disabled={maintenanceLoading}

                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                    >
                      Recargar
                    </button>
                    <button
                      onClick={saveMaintenanceConfig}
                      disabled={maintenanceLoading}
                      className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold"
                    >
                      {maintenanceLoading ? 'Guardando...' : 'Guardar Configuración'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}


          {activeTab === 'pricing' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Configuración de Precios de Licencias</h2>
                <p className="text-gray-600">Define los precios de cada plan y costos adicionales</p>
              </div>
              
              {/* Redirect to dedicated page */}
              <div className="bg-white rounded-lg shadow p-6">
                <button
                  onClick={() => window.location.href = '/license-pricing'}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Ir a Configuración de Precios
                </button>
              </div>
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">📊 Analytics de Licencias</h2>
                <p className="text-gray-600">Análisis completo de suscripciones, ingresos y proyecciones</p>
              </div>

              {analyticsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Cargando datos...</p>
                </div>
              ) : analyticsData ? (
                <div className="space-y-6">
                  {/* SECCIÓN 1: VISTA GENERAL */}
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">📈 Vista General</h3>
                    
                    {/* Métricas Principales */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-600">Total Usuarios</h4>
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <p className="text-3xl font-bold text-blue-600">{analyticsData.summary.total_users}</p>
                        <p className="text-xs text-gray-500 mt-1">Con plan seleccionado</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-600">Usuarios FREE</h4>
                          <Users className="w-5 h-5 text-gray-600" />
                        </div>
                        <p className="text-3xl font-bold text-gray-600">{analyticsData.summary.free_users}</p>
                        <p className="text-xs text-gray-500 mt-1">Sin pago</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-600">Usuarios de Pago</h4>
                          <CreditCard className="w-5 h-5 text-green-600" />
                        </div>
                        <p className="text-3xl font-bold text-green-600">{analyticsData.summary.paying_users}</p>
                        <p className="text-xs text-gray-500 mt-1">Generando ingresos</p>
                      </div>
                      
                      <div className="bg-white rounded-lg p-6 shadow-sm">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-gray-600">Usuarios Trial</h4>
                          <Activity className="w-5 h-5 text-yellow-600" />
                        </div>
                        <p className="text-3xl font-bold text-yellow-600">{analyticsData.summary.trial_users || 0}</p>
                        <p className="text-xs text-gray-500 mt-1">En periodo de prueba</p>
                      </div>
                    </div>

                    {/* Ingresos */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg p-6 text-white shadow-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <DollarSign className="w-6 h-6" />
                          <h4 className="text-lg font-semibold">Ingresos Mensuales Recurrentes</h4>
                        </div>
                        <p className="text-4xl font-bold mb-2">${analyticsData.summary.total_monthly_revenue.toFixed(2)}</p>
                        <p className="text-sm opacity-90">MRR (Monthly Recurring Revenue)</p>
                      </div>
                      
                      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg p-6 text-white shadow-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-6 h-6" />
                          <h4 className="text-lg font-semibold">Ingresos Lifetime</h4>
                        </div>
                        <p className="text-4xl font-bold mb-2">${analyticsData.summary.total_lifetime_revenue.toFixed(2)}</p>
                        <p className="text-sm opacity-90">Pagos únicos acumulados</p>
                      </div>
                    </div>

                    {/* Gráficas */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                      {/* Gráfica de Barras - Usuarios por Plan */}
                      <div className="bg-white rounded-lg p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Usuarios por Tipo de Plan</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={Object.keys(analyticsData.by_plan_type).map(key => ({
                            name: key.replace(/_/g, ' ').toUpperCase(),
                            usuarios: analyticsData.by_plan_type[key].count,
                            ingresos: analyticsData.by_plan_type[key].monthly_revenue
                          }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} fontSize={12} />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="usuarios" fill="#3B82F6" name="Usuarios" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Gráfica de Pastel - Distribución de Ingresos */}
                      <div className="bg-white rounded-lg p-6 shadow-sm">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Distribución de Ingresos Mensuales</h4>
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={Object.keys(analyticsData.by_plan_type)
                                .filter(key => analyticsData.by_plan_type[key].monthly_revenue > 0)
                                .map((key, index) => ({
                                  name: key.replace(/_/g, ' ').toUpperCase(),
                                  value: analyticsData.by_plan_type[key].monthly_revenue
                                }))}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={(entry) => `${entry.name}: $${entry.value.toFixed(0)}`}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {Object.keys(analyticsData.by_plan_type).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][index % 5]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* SECCIÓN 2: DESGLOSE DETALLADO */}
                  <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">📋 Desglose Detallado por Usuario</h3>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b-2 border-gray-200">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Usuario</th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plan</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Modo Pago</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Routers</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Sellers</th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Precio</th>
                            <th className="text-center py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analyticsData.user_details.map((user, index) => (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div className="text-sm font-medium text-gray-900">{user.full_name || user.email}</div>
                                <div className="text-xs text-gray-500">{user.email}</div>
                              </td>
                              <td className="py-3 px-4">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                  {user.plan_type.replace(/_/g, ' ').toUpperCase()}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  user.payment_mode === 'monthly' ? 'bg-green-100 text-green-800' :
                                  user.payment_mode === 'lifetime' ? 'bg-purple-100 text-purple-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.payment_mode === 'monthly' ? 'Mensual' :
                                   user.payment_mode === 'lifetime' ? 'Lifetime' :
                                   user.payment_mode}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4 text-sm text-gray-900">{user.total_routers}</td>
                              <td className="text-center py-3 px-4 text-sm text-gray-900">{user.extra_sellers}</td>
                              <td className="text-right py-3 px-4">
                                <span className={`text-sm font-semibold ${user.price === 0 ? 'text-gray-500' : 'text-green-600'}`}>
                                  ${user.price.toFixed(2)}
                                  {user.payment_mode === 'monthly' && user.price > 0 && (
                                    <span className="text-xs text-gray-500 ml-1">/mes</span>
                                  )}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                  user.status === 'active' ? 'bg-green-100 text-green-800' :
                                  user.status === 'trial' ? 'bg-yellow-100 text-yellow-800' :
                                  user.status === 'suspended' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {user.status === 'active' ? 'Activo' :
                                   user.status === 'trial' ? 'Trial' :
                                   user.status === 'suspended' ? 'Suspendido' :
                                   user.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Resumen de la tabla */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.keys(analyticsData.by_plan_type).map((planType, index) => {
                        const planData = analyticsData.by_plan_type[planType];
                        return (
                          <div key={planType} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                            <h5 className="text-sm font-semibold text-gray-700 mb-2">
                              {planType.replace(/_/g, ' ').toUpperCase()}
                            </h5>
                            <div className="space-y-1 text-sm text-gray-600">
                              <p>👥 {planData.count} usuarios</p>
                              <p>📅 {planData.monthly_users} mensuales</p>
                              <p>♾️ {planData.lifetime_users} lifetime</p>
                              <p className="font-semibold text-green-600">💰 ${planData.monthly_revenue.toFixed(2)}/mes</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* SECCIÓN 3: PROYECCIONES */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-6 border border-green-200">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">💡 Proyecciones y Métricas Clave</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {/* MRR Actual */}
                      <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-green-500">
                        <h4 className="text-sm font-medium text-gray-600 mb-2">💵 MRR Actual</h4>
                        <p className="text-3xl font-bold text-green-600 mb-1">
                          ${analyticsData.summary.total_monthly_revenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Ingresos mensuales recurrentes</p>
                      </div>

                      {/* Ingresos Potenciales de Trials */}
                      <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-yellow-500">
                        <h4 className="text-sm font-medium text-gray-600 mb-2">🎯 Potencial de Trials</h4>
                        <p className="text-3xl font-bold text-yellow-600 mb-1">
                          ${analyticsData.summary.potential_monthly_from_trials.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">
                          Si {analyticsData.summary.trial_users} trials se convierten
                        </p>
                      </div>

                      {/* MRR Proyectado */}
                      <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-purple-500">
                        <h4 className="text-sm font-medium text-gray-600 mb-2">📈 MRR Proyectado</h4>
                        <p className="text-3xl font-bold text-purple-600 mb-1">
                          ${(analyticsData.summary.total_monthly_revenue + analyticsData.summary.potential_monthly_from_trials).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Si todos los trials convierten</p>
                      </div>

                      {/* Tasa de Conversión */}
                      <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-blue-500">
                        <h4 className="text-sm font-medium text-gray-600 mb-2">📊 Usuarios Activos vs Trial</h4>
                        <p className="text-3xl font-bold text-blue-600 mb-1">
                          {analyticsData.summary.active_users || 0}
                        </p>
                        <p className="text-xs text-gray-500">
                          De {analyticsData.summary.total_users} usuarios totales
                        </p>
                      </div>

                      {/* Ingresos Lifetime Acumulados */}
                      <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-indigo-500">
                        <h4 className="text-sm font-medium text-gray-600 mb-2">♾️ Lifetime Acumulado</h4>
                        <p className="text-3xl font-bold text-indigo-600 mb-1">
                          ${analyticsData.summary.total_lifetime_revenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Pagos únicos recibidos</p>
                      </div>

                      {/* Proyección Anual */}
                      <div className="bg-white rounded-lg p-6 shadow-sm border-l-4 border-orange-500">
                        <h4 className="text-sm font-medium text-gray-600 mb-2">📅 Proyección Anual</h4>
                        <p className="text-3xl font-bold text-orange-600 mb-1">
                          ${(analyticsData.summary.total_monthly_revenue * 12).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">MRR × 12 meses</p>
                      </div>
                    </div>

                    {/* Distribución por Status */}
                    <div className="mt-6 bg-white rounded-lg p-6 shadow-sm">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Estado de Licencias</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.keys(analyticsData.by_status).map((status) => {
                          const statusData = analyticsData.by_status[status];
                          const statusColors = {
                            trial: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '🔄' },
                            active: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅' },
                            suspended: { bg: 'bg-red-100', text: 'text-red-800', icon: '⛔' },
                            expired: { bg: 'bg-gray-100', text: 'text-gray-800', icon: '⏰' }
                          };
                          const colors = statusColors[status] || { bg: 'bg-gray-100', text: 'text-gray-800', icon: '❓' };
                          
                          return (
                            <div key={status} className={`${colors.bg} rounded-lg p-4`}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-2xl">{colors.icon}</span>
                                <h5 className={`text-sm font-semibold ${colors.text} uppercase`}>
                                  {status}
                                </h5>
                              </div>
                              <p className={`text-2xl font-bold ${colors.text}`}>{statusData.count}</p>
                              {statusData.monthly_revenue > 0 && (
                                <p className="text-xs text-gray-600 mt-1">
                                  ${statusData.monthly_revenue.toFixed(2)}/mes
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                <div className="text-center py-12 bg-white rounded-lg shadow-sm">
                  <PieChartIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay datos de analytics</h3>
                  <p className="text-gray-600">Los datos se cargarán automáticamente.</p>
                </div>
              )}
            </div>
          )}


          {activeTab === 'reports' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Reportes y Análisis</h2>
                
                {/* Comparación Mensual */}
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-lg p-6 mb-6 border border-purple-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Comparación Mensual</h3>
                  <p className="text-sm text-gray-600 mb-4">Compara el rendimiento del mes actual con el mes anterior</p>
                  <button
                    onClick={handleMonthlyComparison}
                    disabled={comparisonLoading}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4" />
                    {comparisonLoading ? 'Generando...' : 'Ver Comparación Mes Actual vs Anterior'}
                  </button>
                </div>

                {/* Selección de Métricas */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Seleccionar Métricas a Mostrar</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMetrics.newRegistrations}
                        onChange={() => toggleMetric('newRegistrations')}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Clientes Nuevos</span>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMetrics.licensesActivated}
                        onChange={() => toggleMetric('licensesActivated')}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Licencias Activadas</span>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMetrics.licensesDeleted}
                        onChange={() => toggleMetric('licensesDeleted')}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Licencias Eliminadas</span>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMetrics.paymentsApproved}
                        onChange={() => toggleMetric('paymentsApproved')}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Pagos Aprobados</span>
                    </label>
                    
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedMetrics.totalRevenue}
                        onChange={() => toggleMetric('totalRevenue')}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">Total Recaudado</span>
                    </label>
                  </div>
                </div>

                {/* Reporte por Rango de Fechas */}
                <div className="bg-gray-50 rounded-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Reporte por Rango de Fechas</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha Inicio
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha Fin
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="flex items-end">
                      <button
                        onClick={handleGenerateReport}
                        disabled={reportLoading || !startDate || !endDate}
                        className="w-full px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        <FileText className="w-4 h-4" />
                        {reportLoading ? 'Generando...' : 'Generar Reporte'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mostrar Comparación Mensual */}
                {monthlyComparison && (
                  <div>
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Calendar className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          Comparación: {monthlyComparison.current_month.name} vs {monthlyComparison.previous_month.name}
                        </h3>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Clientes Nuevos */}
                      {selectedMetrics.newRegistrations && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="text-sm font-medium text-gray-600 mb-3">Clientes Nuevos</h4>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-3xl font-bold text-gray-900">{monthlyComparison.current_month.new_registrations}</p>
                              <p className="text-sm text-gray-500">Mes actual</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-semibold text-gray-600">{monthlyComparison.previous_month.new_registrations}</p>
                              <p className="text-sm text-gray-500">Mes anterior</p>
                            </div>
                          </div>
                          <div className={`mt-3 text-sm font-medium ${monthlyComparison.comparison.new_registrations_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {monthlyComparison.comparison.new_registrations_change >= 0 ? '↑' : '↓'} {Math.abs(monthlyComparison.comparison.new_registrations_change)} ({monthlyComparison.comparison.new_registrations_percent}%)
                          </div>
                        </div>
                      )}

                      {/* Licencias Activadas */}
                      {selectedMetrics.licensesActivated && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="text-sm font-medium text-gray-600 mb-3">Licencias Activadas</h4>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-3xl font-bold text-green-600">{monthlyComparison.current_month.licenses_activated}</p>
                              <p className="text-sm text-gray-500">Mes actual</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-semibold text-gray-600">{monthlyComparison.previous_month.licenses_activated}</p>
                              <p className="text-sm text-gray-500">Mes anterior</p>
                            </div>
                          </div>
                          <div className={`mt-3 text-sm font-medium ${monthlyComparison.comparison.licenses_activated_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {monthlyComparison.comparison.licenses_activated_change >= 0 ? '↑' : '↓'} {Math.abs(monthlyComparison.comparison.licenses_activated_change)} ({monthlyComparison.comparison.licenses_activated_percent}%)
                          </div>
                        </div>
                      )}

                      {/* Licencias Eliminadas */}
                      {selectedMetrics.licensesDeleted && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="text-sm font-medium text-gray-600 mb-3">Licencias Eliminadas</h4>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-3xl font-bold text-red-600">{monthlyComparison.current_month.licenses_deleted}</p>
                              <p className="text-sm text-gray-500">Mes actual</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-semibold text-gray-600">{monthlyComparison.previous_month.licenses_deleted}</p>
                              <p className="text-sm text-gray-500">Mes anterior</p>
                            </div>
                          </div>
                          <div className={`mt-3 text-sm font-medium ${monthlyComparison.comparison.licenses_deleted_change <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {monthlyComparison.comparison.licenses_deleted_change >= 0 ? '↑' : '↓'} {Math.abs(monthlyComparison.comparison.licenses_deleted_change)} ({monthlyComparison.comparison.licenses_deleted_percent}%)
                          </div>
                        </div>
                      )}

                      {/* Total Recaudado */}
                      {selectedMetrics.totalRevenue && (
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                          <h4 className="text-sm font-medium mb-3 opacity-90">Total Recaudado</h4>
                          <div className="flex items-end justify-between">
                            <div>
                              <p className="text-3xl font-bold">${monthlyComparison.current_month.total_revenue.toFixed(2)}</p>
                              <p className="text-sm opacity-90">Mes actual</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-semibold">${monthlyComparison.previous_month.total_revenue.toFixed(2)}</p>
                              <p className="text-sm opacity-90">Mes anterior</p>
                            </div>
                          </div>
                          <div className="mt-3 text-sm font-medium">
                            {monthlyComparison.comparison.revenue_change >= 0 ? '↑' : '↓'} ${Math.abs(monthlyComparison.comparison.revenue_change).toFixed(2)} ({monthlyComparison.comparison.revenue_percent}%)
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Mostrar Reporte por Rango de Fechas */}
                {reportData && (
                  <div>
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <Calendar className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-semibold text-gray-900">
                          Período: {new Date(reportData.period.start).toLocaleDateString('es-MX')} - {new Date(reportData.period.end).toLocaleDateString('es-MX')}
                        </h3>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      {selectedMetrics.newRegistrations && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="text-sm font-medium text-gray-600 mb-2">Nuevos Registros</h4>
                          <p className="text-3xl font-bold text-blue-600">{reportData.new_registrations}</p>
                        </div>
                      )}
                      
                      {selectedMetrics.licensesActivated && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="text-sm font-medium text-gray-600 mb-2">Licencias Activadas</h4>
                          <p className="text-3xl font-bold text-green-600">{reportData.licenses_activated}</p>
                        </div>
                      )}
                      
                      {selectedMetrics.licensesDeleted && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="text-sm font-medium text-gray-600 mb-2">Licencias Eliminadas</h4>
                          <p className="text-3xl font-bold text-red-600">{reportData.licenses_deleted}</p>
                        </div>
                      )}
                      
                      {selectedMetrics.paymentsApproved && (
                        <div className="bg-white border border-gray-200 rounded-lg p-6">
                          <h4 className="text-sm font-medium text-gray-600 mb-2">Pagos Aprobados</h4>
                          <p className="text-3xl font-bold text-orange-600">{reportData.payments_approved}</p>
                        </div>
                      )}
                    </div>

                    {selectedMetrics.totalRevenue && (
                      <div className="grid grid-cols-1 gap-4">
                        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
                          <h4 className="text-sm font-medium mb-2 opacity-90">Ingresos Totales</h4>
                          <p className="text-3xl font-bold">${reportData.total_revenue.toFixed(2)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {!reportData && !monthlyComparison && !reportLoading && !comparisonLoading && (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">No hay reporte generado</h3>
                    <p className="text-gray-600">Selecciona un rango de fechas o genera una comparación mensual para ver las estadísticas.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Nueva Sección: Reportes SuperAdmin */}
          {activeTab === 'superadmin-reports' && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">📊 Reportes del Sistema</h2>
                <p className="text-gray-600">Análisis y métricas del sistema completo</p>
              </div>

              {reportsLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Cargando reportes...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  
                  {/* Reporte 1: Licencias Activadas */}
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                    <div className="bg-gradient-to-r from-blue-500 to-indigo-500 p-6 rounded-t-lg">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Shield className="w-6 h-6" />
                        Licencias Activadas
                      </h3>
                    </div>
                    <div className="p-6">
                      {reportsData.activatedLicenses ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                              <p className="text-sm text-gray-600 mb-1">Total de Licencias</p>
                              <p className="text-3xl font-bold text-blue-600">{reportsData.activatedLicenses.total_licenses}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                              <p className="text-sm text-gray-600 mb-1">Licencias Activas</p>
                              <p className="text-3xl font-bold text-green-600">{reportsData.activatedLicenses.active_licenses}</p>
                            </div>
                            <div className="bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
                              <p className="text-sm text-gray-600 mb-1">Licencias en Trial</p>
                              <p className="text-3xl font-bold text-yellow-600">{reportsData.activatedLicenses.trial_licenses}</p>
                            </div>
                          </div>
                          
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50 border-b-2 border-gray-200">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cliente</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Plan</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Routers</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Activación</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Expiración</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {reportsData.activatedLicenses.licenses.map((lic, idx) => (
                                  <tr key={idx} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{lic.user_name}</td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{lic.user_email}</td>
                                    <td className="px-4 py-3 text-sm text-gray-700 font-medium">{lic.plan_type}</td>
                                    <td className="px-4 py-3 text-center">
                                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                        lic.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                      }`}>
                                        {lic.status === 'active' ? 'Activa' : 'Trial'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center text-sm">
                                      <span className="font-semibold text-blue-600">{lic.routers_configured}</span>
                                      <span className="text-gray-500">/{lic.total_routers}</span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {lic.activation_date ? new Date(lic.activation_date).toLocaleDateString('es-MX') : 'N/A'}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">
                                      {lic.expiry_date ? new Date(lic.expiry_date).toLocaleDateString('es-MX') : 'N/A'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No hay datos disponibles</p>
                      )}
                    </div>
                  </div>

                  {/* Reporte 2: Versiones de MikroTik */}
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 rounded-t-lg">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Activity className="w-6 h-6" />
                        Versiones de MikroTik RouterOS
                      </h3>
                    </div>
                    <div className="p-6">
                      {reportsData.mikrotikVersions ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                              <p className="text-sm text-gray-600 mb-1">Total de Routers</p>
                              <p className="text-3xl font-bold text-gray-700">{reportsData.mikrotikVersions.total_routers}</p>
                              <p className="text-xs text-gray-500 mt-1">Reservados en sistema</p>
                            </div>
                            <div className="bg-teal-50 rounded-lg p-4 border-2 border-teal-200">
                              <p className="text-sm text-gray-600 mb-1">Configurados</p>
                              <p className="text-3xl font-bold text-teal-600">{reportsData.mikrotikVersions.configured_routers}</p>
                              <p className="text-xs text-gray-500 mt-1">Con versión detectada</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                              <p className="text-sm text-gray-600 mb-1">RouterOS v6</p>
                              <p className="text-3xl font-bold text-blue-600">{reportsData.mikrotikVersions.v6_count}</p>
                              <p className="text-xs text-gray-500 mt-1">{reportsData.mikrotikVersions.v6_percentage}% de configurados</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                              <p className="text-sm text-gray-600 mb-1">RouterOS v7</p>
                              <p className="text-3xl font-bold text-green-600">{reportsData.mikrotikVersions.v7_count}</p>
                              <p className="text-xs text-gray-500 mt-1">{reportsData.mikrotikVersions.v7_percentage}% de configurados</p>
                            </div>
                            <div className="bg-orange-50 rounded-lg p-4 border-2 border-orange-200">
                              <p className="text-sm text-gray-600 mb-1">Sin Configurar</p>
                              <p className="text-3xl font-bold text-orange-600">{reportsData.mikrotikVersions.not_configured_routers}</p>
                              <p className="text-xs text-gray-500 mt-1">Pendientes</p>
                            </div>
                          </div>

                          {/* Detalles de v6 */}
                          {reportsData.mikrotikVersions.v6_count > 0 && (
                            <div className="mb-6">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">v6</span>
                                Routers con RouterOS v6
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {reportsData.mikrotikVersions.v6_routers.map((router, idx) => (
                                  <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <p className="font-semibold text-gray-900">{router.router_name}</p>
                                    <p className="text-sm text-gray-600">Versión: {router.version}</p>
                                    <p className="text-xs text-gray-500">Cliente: {router.user_name}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Detalles de v7 */}
                          {reportsData.mikrotikVersions.v7_count > 0 && (
                            <div className="mb-6">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">v7</span>
                                Routers con RouterOS v7
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {reportsData.mikrotikVersions.v7_routers.map((router, idx) => (
                                  <div key={idx} className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <p className="font-semibold text-gray-900">{router.router_name}</p>
                                    <p className="text-sm text-gray-600">Versión: {router.version}</p>
                                    <p className="text-xs text-gray-500">Cliente: {router.user_name}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No hay datos disponibles</p>
                      )}
                    </div>
                  </div>

                  {/* Reporte 3: Licencias Activas y sus Routers */}
                  <div className="bg-white rounded-lg shadow-lg border border-gray-200">
                    <div className="bg-gradient-to-r from-teal-500 to-cyan-500 p-6 rounded-t-lg">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <Users className="w-6 h-6" />
                        Licencias Activas y Routers
                      </h3>
                    </div>
                    <div className="p-6">
                      {reportsData.licensesRouters ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-teal-50 rounded-lg p-4 border-2 border-teal-200">
                              <p className="text-sm text-gray-600 mb-1">Total Licencias</p>
                              <p className="text-3xl font-bold text-teal-600">{reportsData.licensesRouters.total_licenses}</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
                              <p className="text-sm text-gray-600 mb-1">Total Routers</p>
                              <p className="text-3xl font-bold text-blue-600">{reportsData.licensesRouters.total_routers}</p>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                              <p className="text-sm text-gray-600 mb-1">Routers Configurados</p>
                              <p className="text-3xl font-bold text-green-600">{reportsData.licensesRouters.total_configured}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-4 border-2 border-purple-200">
                              <p className="text-sm text-gray-600 mb-1">% Configuración</p>
                              <p className="text-3xl font-bold text-purple-600">{reportsData.licensesRouters.configuration_percentage}%</p>
                            </div>
                          </div>

                          {/* Lista de Licencias con sus Routers */}
                          <div className="space-y-4">
                            {reportsData.licensesRouters.licenses.map((lic, idx) => (
                              <div key={idx} className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <h4 className="font-bold text-gray-900 text-lg">{lic.user_name}</h4>
                                    <p className="text-sm text-gray-600">{lic.user_email}</p>
                                  </div>
                                  <div className="text-right">
                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                                      lic.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                      {lic.status === 'active' ? 'Activa' : 'Trial'}
                                    </span>
                                    <p className="text-sm text-gray-500 mt-1">Plan: {lic.plan_type}</p>
                                  </div>
                                </div>
                                
                                <div className="flex items-center gap-2 mb-3">
                                  <Activity className="w-4 h-4 text-gray-500" />
                                  <p className="text-sm text-gray-700">
                                    <span className="font-semibold text-blue-600">{lic.routers_configured}</span> de{' '}
                                    <span className="font-semibold">{lic.total_routers}</span> routers configurados
                                  </p>
                                </div>

                                {/* Lista de routers de esta licencia */}
                                {lic.routers && lic.routers.length > 0 && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                                    {lic.routers.map((router, ridx) => (
                                      <div key={ridx} className={`rounded-lg p-3 border-2 ${
                                        router.configured && router.is_active 
                                          ? 'bg-green-50 border-green-300' 
                                          : 'bg-gray-100 border-gray-300'
                                      }`}>
                                        <div className="flex items-center justify-between mb-1">
                                          <p className="font-semibold text-sm text-gray-900">{router.router_name}</p>
                                          {router.configured && router.is_active ? (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                          ) : (
                                            <XCircle className="w-4 h-4 text-gray-400" />
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-600">Versión: {router.version || 'N/A'} ({router.version_type || 'unknown'})</p>
                                        <p className="text-xs text-gray-500 font-mono">{router.vpn_ip}:{router.vpn_port}</p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </>
                      ) : (
                        <p className="text-gray-500 text-center py-8">No hay datos disponibles</p>
                      )}
                    </div>
                  </div>

                </div>
              )}
            </div>
          )}

          {activeTab === "payments" && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">💳 Solicitudes de Pago</h2>
                <p className="text-gray-600">Gestiona y aprueba solicitudes de los usuarios</p>
              </div>
              
              <PaymentRequestsManagement />
            </div>
          )}
        </div>
      </div>
      
      {/* User Search Modal */}
      <UserSearchModal 
        isOpen={showUserSearchModal} 
        onClose={() => setShowUserSearchModal(false)} 
      />
    </div>
  );
};

export default SuperAdminDashboard;

